// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// A mechanical/electrical unit on the plan (CRAH, PDU, UPS lineup). Colour
// comes from the unit *kind* so the legend reads straight across; running
// state is the status dot, so a fleet of blue boxes still shows which one is
// in alarm. The second line is the unit's live reading — an em dash until the
// twin publishes one. `onSelect` is the seam for opening the asset detail
// panel once units carry twin asset ids.

import React from 'react';
import type { EquipmentStatus } from '../../../types/alarm';
import type { PlanUnit, PlanUnitKind } from '../../../types/floorPlan';

const STATUS_LABEL: Record<EquipmentStatus, string> = {
  ok: 'running',
  warn: 'warning',
  fault: 'fault',
  idle: 'idle',
};

const DOT_INSET = 5;
const NO_READING = '–';

/** Kind glyph, drawn at (0,0) and translated into the unit body. */
function KindGlyph({ kind }: { kind: PlanUnitKind }) {
  if (kind === 'crah') {
    // Snowflake: three crossed strokes — cooling.
    return (
      <g className="plan-unit-glyph">
        <line x1={0} y1={-3.2} x2={0} y2={3.2} />
        <line x1={-2.8} y1={-1.6} x2={2.8} y2={1.6} />
        <line x1={-2.8} y1={1.6} x2={2.8} y2={-1.6} />
      </g>
    );
  }
  // Bolt: PDU and UPS lineups both carry power.
  return (
    <g className="plan-unit-glyph plan-unit-glyph-solid">
      <polygon points="1.4,-3.4 -2,0.4 0,0.4 -1.4,3.4 2,-0.6 0,-0.6" />
    </g>
  );
}

export default function PlanUnitSymbol({
  unit,
  onSelect,
}: {
  unit: PlanUnit;
  onSelect?: (_unitId: string) => void;
}) {
  const interactive = Boolean(onSelect) && !unit.future;
  const label = unit.future ? `(F) ${unit.label}` : unit.label;
  const reading = unit.reading ?? NO_READING;

  return (
    <g
      className={`plan-unit plan-unit-${unit.kind} plan-unit-status-${unit.status}${
        unit.future ? ' plan-unit-future' : ''
      }${interactive ? ' plan-unit-interactive' : ''}`}
      data-testid={`plan-unit-${unit.id}`}
      role={interactive ? 'button' : 'img'}
      tabIndex={interactive ? 0 : undefined}
      aria-label={`${label} — ${STATUS_LABEL[unit.status]}`}
      onClick={interactive ? () => onSelect?.(unit.id) : undefined}
      onKeyDown={
        interactive
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(unit.id);
              }
            }
          : undefined
      }
    >
      <rect x={unit.x} y={unit.y} width={unit.w} height={unit.h} className="plan-unit-fill" />
      <g transform={`translate(${unit.x + 8}, ${unit.y + 10})`}>
        <KindGlyph kind={unit.kind} />
      </g>
      <text
        x={unit.x + unit.w / 2 + 4}
        y={unit.y + 12}
        className="plan-unit-label"
        textAnchor="middle"
      >
        {label}
      </text>
      <text
        x={unit.x + unit.w / 2}
        y={unit.y + 21}
        className="plan-unit-reading"
        textAnchor="middle"
      >
        {reading}
      </text>
      {!unit.future && (
        <circle
          cx={unit.x + unit.w - DOT_INSET}
          cy={unit.y + DOT_INSET}
          r={2.2}
          className="plan-unit-dot"
        />
      )}
    </g>
  );
}
